// BlockCard — primitive de bloc PARTAGÉE de la fiche dossier (chantier style 1/3).
// Unifie les deux primitives historiques :
//   - Block (InfosTab.jsx)        → header statique + badge/action
//   - Card  (OperationSection.jsx) → mode repliable <details> + accent FK bleu
// Géométrie = mockup (docs/upload/mockup-*.html) : bordure 1px var(--oh-border),
// radius 8px, margin-bottom 10px, header 11px 14px (titre 14px medium + badge),
// contenu 0 14px 13px, AUCUNE box-shadow. Tokens var(--oh-*) réutilisés.
//
// Props :
//   title       : noeud titre (texte ou JSX, peut inclure une icône inline)
//   icon        : composant icône lucide (optionnel) rendu avant le titre
//   badge       : noeud badge (complétude, statut…) à droite
//   action      : noeud action (bouton Modifier…) à droite du badge
//   accent      : bool — bloc à sélecteur FK éditable → liseré + fond bleutés
//   collapsible : bool — header en <summary> repliable (+ chevron)
//   open        : bool — état contrôlé du <details> (optionnel ; sinon non contrôlé,
//                 replié par défaut). Permet de déplier programmatiquement (ex. à l'édition).
//   onToggle    : handler du toggle natif (sync l'état contrôlé : e.currentTarget.open)
//   children    : contenu du bloc
//   testId      : data-testid
//   iconColor   : couleur de l'icône (défaut : bleu si accent, sinon muted)
//   marginBottom: marge basse en px (défaut 10 ; passer 0 si le conteneur gère
//                 l'espacement via space-y pour éviter le double écart)
import { ChevronDown } from "lucide-react";

export function BlockCard({
  title,
  icon: Icon,
  badge,
  action,
  accent = false,
  collapsible = false,
  open,
  onToggle,
  children,
  testId,
  iconColor,
  marginBottom = 10,
}) {
  // accent (mockup) = bloc FK → liseré + fond bleutés ; sinon surface élevée neutre.
  const containerStyle = {
    background: accent ? "var(--oh-stat-blue-bg)" : "var(--oh-bg-elevated)",
    border: `1px solid ${accent ? "var(--oh-stat-blue-border)" : "var(--oh-border)"}`,
    borderRadius: 8,
    marginBottom,
  };
  const resolvedIconColor = iconColor || (accent ? "var(--oh-stat-blue-text)" : "var(--oh-text-muted)");

  const titleNode = (
    <span className="text-sm font-medium flex items-center gap-2 min-w-0" style={{ color: "var(--oh-text)" }}>
      {Icon && <Icon className="w-4 h-4 flex-shrink-0" style={{ color: resolvedIconColor }} />}
      {title}
    </span>
  );
  // En mode collapsible, le header est un <summary> : un clic sur l'action
  // (ex. « Modifier » ANAH) déclencherait aussi le repli/dépli. On annule ce
  // toggle via preventDefault SANS toucher au onClick câblé de l'action (qui,
  // lui, reste « tel quel »). Hors collapsible, l'action est rendue telle quelle.
  const actionNode = action && collapsible
    ? <span onClick={(e) => e.preventDefault()}>{action}</span>
    : action;
  const rightNode = (
    <span className="flex items-center gap-2 flex-shrink-0">
      {badge}
      {actionNode}
      {collapsible && <ChevronDown className="w-4 h-4" style={{ color: "var(--oh-text-muted)" }} />}
    </span>
  );

  const headerStyle = { padding: "11px 14px" };
  const contentStyle = { padding: "0 14px 13px" };

  if (collapsible) {
    return (
      <div className="overflow-hidden" style={containerStyle} data-testid={testId}>
        <details open={open} onToggle={onToggle}>
          <summary className="flex items-center justify-between gap-2 cursor-pointer select-none" style={headerStyle}>
            {titleNode}
            {rightNode}
          </summary>
          <div style={contentStyle}>{children}</div>
        </details>
      </div>
    );
  }

  return (
    <div className="overflow-hidden" style={containerStyle} data-testid={testId}>
      <div className="flex items-center justify-between gap-2" style={headerStyle}>
        {titleNode}
        {rightNode}
      </div>
      <div style={contentStyle}>{children}</div>
    </div>
  );
}

export default BlockCard;
